import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  FunnelIcon,
  ListBulletIcon,
  PlusIcon,
  Squares2X2Icon,
  XMarkIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { usePersistedPreference } from '../hooks/useFilterPersistence';
import { areasService, OrganizationArea } from '../services/areas';
import { companiesService, Company } from '../services/companies';
import { groupsService } from '../services/groups';
import { MasterPlanTask, masterPlanTaskService } from '../services/masterPlan';
import { propositionService } from '../services/propositions';
import { segmentService } from '../services/segments';
import TaskKanbanView from '../components/GravityPlan/TaskKanbanView';
import TaskListView from '../components/GravityPlan/TaskListView';
import TaskModal from '../components/TaskModal';
import { LoadingProgress } from '../components/LoadingSkeletons';
import { MultiSelectDropdown } from '../components/MultiSelectDropdown';
import { API_URL } from '../constants/urls';

interface OrganizationMember {
  user_id: string;
  user_name: string | null;
  user_email: string;
  role: string;
  full_name?: string;
}

interface Position {
  id: string;
  segment_id: string;
  segment_name: string;
  proposition_id: string;
  proposition_name: string;
}

const MyTasks: React.FC = () => {
  const { selectedOrg, canEdit, isAdmin } = useOrganization();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Loading your tasks...');
  const [tasks, setTasks] = useState<MasterPlanTask[]>([]);
  const [organizationMembers, setOrganizationMembers] = useState<
    OrganizationMember[]
  >([]);
  const [areas, setAreas] = useState<OrganizationArea[]>([]);
  const [viewMode, setViewMode] = usePersistedPreference<'list' | 'kanban'>(
    'viewMode',
    'kanban',
    'myTasks',
  );
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState<
    'all' | 'overdue' | 'dueToday' | 'dueThisWeek'
  >('all');
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [selectedOKRs, setSelectedOKRs] = useState<string[]>([]);
  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
  const [selectedPropositions, setSelectedPropositions] = useState<string[]>(
    [],
  );
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Data for context
  const [companies, setCompanies] = useState<Company[]>([]);
  const [segments, setSegments] = useState<any[]>([]);
  const [propositions, setPropositions] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [allOKRs, setAllOKRs] = useState<any[]>([]);

  // Fetch all necessary data
  const fetchData = useCallback(async () => {
    if (!selectedOrg || !user) return;

    setLoading(true);
    setLoadingMessage('Loading your tasks...');

    try {
      const headers = {
        Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
      };

      // Fetch tasks - filtered to current user
      const tasksResponse = await masterPlanTaskService.list(selectedOrg.id);
      const userTasks = tasksResponse.filter(
        (task: MasterPlanTask) => task.assigned_to_id === user.id,
      );
      setTasks(userTasks);

      setLoadingMessage('Loading organization data...');

      // Fetch supporting data in parallel
      const [
        membersResponse,
        areasResponse,
        companiesData,
        segmentsData,
        propositionsData,
        groupsData,
        positionsResponse,
      ] = await Promise.all([
        axios.get(`${API_URL}/organizations/${selectedOrg.id}/members`, {
          headers,
        }),
        areasService.list(selectedOrg.id),
        companiesService.list(selectedOrg.id),
        segmentService.list(selectedOrg.id),
        propositionService.list(selectedOrg.id),
        groupsService.getGroups(selectedOrg.id),
        axios.get(`${API_URL}/positions?organization_id=${selectedOrg.id}`, {
          headers,
        }),
      ]);

      setOrganizationMembers(membersResponse.data);
      setAreas(areasResponse);
      setCompanies(companiesData);
      setSegments(segmentsData);
      setPropositions(propositionsData);
      setGroups(groupsData);
      setPositions(positionsResponse.data);

      // Fetch OKRs for context
      try {
        const okrsResponse = await axios.get(
          `${API_URL}/okrs?organization_id=${selectedOrg.id}`,
          { headers },
        );
        setAllOKRs(okrsResponse.data);
      } catch (error) {
        console.error('Error fetching OKRs:', error);
        setAllOKRs([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter tasks
  const filteredTasks = useMemo(() => {
    let filtered = [...tasks];

    const getContextType = (task: MasterPlanTask) =>
      task.context_object_type || task.context_type;

    // Filter by area
    if (selectedAreas.length > 0) {
      filtered = filtered.filter(
        (task) => task.area_id && selectedAreas.includes(task.area_id),
      );
    }

    // Filter by status
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((task) =>
        selectedStatuses.includes(task.status),
      );
    }

    // Filter by company context
    if (selectedCompanies.length > 0) {
      filtered = filtered.filter((task) => {
        const contextType = getContextType(task);
        return (
          contextType === 'company' &&
          task.context_object_id &&
          selectedCompanies.includes(task.context_object_id)
        );
      });
    }

    // Filter by OKR
    if (selectedOKRs.length > 0) {
      filtered = filtered.filter((task) => {
        return task.okr_id && selectedOKRs.includes(task.okr_id);
      });
    }

    // Filter by segment context or positions tied to the segment
    if (selectedSegments.length > 0) {
      filtered = filtered.filter((task) => {
        const contextType = getContextType(task);
        if (!contextType) return false;

        if (contextType === 'segment') {
          return (
            task.context_object_id &&
            selectedSegments.includes(task.context_object_id)
          );
        }

        if (contextType === 'position') {
          const position = positions.find(
            (pos) => pos.id === task.context_object_id,
          );
          return (
            position?.segment_id &&
            selectedSegments.includes(position.segment_id)
          );
        }

        return false;
      });
    }

    // Filter by proposition context or positions tied to the proposition
    if (selectedPropositions.length > 0) {
      filtered = filtered.filter((task) => {
        const contextType = getContextType(task);
        if (!contextType) return false;

        if (contextType === 'proposition') {
          return (
            task.context_object_id &&
            selectedPropositions.includes(task.context_object_id)
          );
        }

        if (contextType === 'position') {
          const position = positions.find(
            (pos) => pos.id === task.context_object_id,
          );
          return (
            position?.proposition_id &&
            selectedPropositions.includes(position.proposition_id)
          );
        }

        return false;
      });
    }

    // Additional time-based filters driven by summary shortcuts
    if (selectedTimeFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const dayOfWeek = today.getDay();
      const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const startOfWeek = new Date(today);
      startOfWeek.setDate(startOfWeek.getDate() - diffToMonday);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);

      filtered = filtered.filter((task) => {
        if (selectedTimeFilter === 'overdue') {
          if (task.status === 'complete' || !task.due_date) return false;
          return new Date(task.due_date) < today;
        }

        if (selectedTimeFilter === 'dueToday') {
          if (task.status === 'complete' || !task.due_date) return false;
          const dueDate = new Date(task.due_date);
          return dueDate.toDateString() === today.toDateString();
        }

        if (selectedTimeFilter === 'dueThisWeek') {
          if (!task.due_date) return false;
          const dueDate = new Date(task.due_date);
          const normalizedDue = new Date(
            dueDate.getFullYear(),
            dueDate.getMonth(),
            dueDate.getDate(),
          );
          return (
            normalizedDue >= startOfWeek &&
            normalizedDue <= endOfWeek &&
            task.status !== 'complete'
          );
        }

        return true;
      });
    }

    return filtered;
  }, [
    tasks,
    selectedAreas,
    selectedStatuses,
    selectedTimeFilter,
    selectedCompanies,
    selectedOKRs,
    selectedSegments,
    selectedPropositions,
    positions,
  ]);

  // Task statistics
  const taskStats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayOfWeek = today.getDay();
    const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const startOfWeek = new Date(today);
    startOfWeek.setDate(startOfWeek.getDate() - diffToMonday);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 6);

    const stats = {
      total: tasks.length,
      pending: tasks.filter((t) => t.status === 'not_started').length,
      inProgress: tasks.filter((t) => t.status === 'in_progress').length,
      completed: tasks.filter((t) => t.status === 'complete').length,
      overdue: tasks.filter((t) => {
        if (t.status === 'complete' || !t.due_date) return false;
        return new Date(t.due_date) < today;
      }).length,
      dueToday: tasks.filter((t) => {
        if (t.status === 'complete' || !t.due_date) return false;
        const dueDate = new Date(t.due_date);
        return dueDate.toDateString() === today.toDateString();
      }).length,
      dueThisWeek: tasks.filter((t) => {
        if (!t.due_date || t.status === 'complete') return false;
        const dueDate = new Date(t.due_date);
        const normalizedDue = new Date(
          dueDate.getFullYear(),
          dueDate.getMonth(),
          dueDate.getDate(),
        );
        return normalizedDue >= startOfWeek && normalizedDue <= endOfWeek;
      }).length,
    };

    return stats;
  }, [tasks]);

  // Handle task updates
  const handleTaskUpdate = useCallback(
    async (taskId: string, updates: Partial<MasterPlanTask>) => {
      try {
        const updatedTask = await masterPlanTaskService.update(
          selectedOrg!.id,
          taskId,
          updates,
        );
        setTasks((prev) =>
          prev.map((task) => (task.id === taskId ? updatedTask : task)),
        );
      } catch (error) {
        console.error('Error updating task:', error);
      }
    },
    [selectedOrg],
  );

  const handleTaskCreate = useCallback(async (task: MasterPlanTask) => {
    setTasks((prev) => [...prev, task]);
    setShowCreateModal(false);
  }, []);

  const handleTaskDelete = useCallback(
    async (taskId: string) => {
      try {
        await masterPlanTaskService.delete(selectedOrg!.id, taskId);
        setTasks((prev) => prev.filter((task) => task.id !== taskId));
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    },
    [selectedOrg],
  );

  const filtersActive =
    selectedAreas.length > 0 ||
    selectedStatuses.length > 0 ||
    selectedTimeFilter !== 'all' ||
    selectedCompanies.length > 0 ||
    selectedOKRs.length > 0 ||
    selectedSegments.length > 0 ||
    selectedPropositions.length > 0;

  const summaryCards = [
    {
      key: 'total',
      label: 'Total',
      value: taskStats.total,
      bgClass: 'bg-gray-50',
      labelClass: 'text-xs text-gray-600',
      valueClass: 'text-lg font-semibold text-gray-900',
      isActive: selectedStatuses.length === 0 && selectedTimeFilter === 'all',
      onClick: () => {
        if (selectedStatuses.length === 0 && selectedTimeFilter === 'all') {
          return;
        }
        setSelectedStatuses([]);
        setSelectedTimeFilter('all');
      },
    },
    {
      key: 'pending',
      label: 'Pending',
      value: taskStats.pending,
      bgClass: 'bg-yellow-50',
      labelClass: 'text-xs text-yellow-700',
      valueClass: 'text-lg font-semibold text-yellow-700',
      isActive:
        selectedStatuses.length === 1 &&
        selectedStatuses[0] === 'not_started' &&
        selectedTimeFilter === 'all',
      onClick: () => {
        if (
          selectedStatuses.length === 1 &&
          selectedStatuses[0] === 'not_started' &&
          selectedTimeFilter === 'all'
        ) {
          setSelectedStatuses([]);
          setSelectedTimeFilter('all');
        } else {
          setSelectedStatuses(['not_started']);
          setSelectedTimeFilter('all');
        }
      },
    },
    {
      key: 'inProgress',
      label: 'In Progress',
      value: taskStats.inProgress,
      bgClass: 'bg-blue-50',
      labelClass: 'text-xs text-blue-700',
      valueClass: 'text-lg font-semibold text-blue-700',
      isActive:
        selectedStatuses.length === 1 &&
        selectedStatuses[0] === 'in_progress' &&
        selectedTimeFilter === 'all',
      onClick: () => {
        if (
          selectedStatuses.length === 1 &&
          selectedStatuses[0] === 'in_progress' &&
          selectedTimeFilter === 'all'
        ) {
          setSelectedStatuses([]);
          setSelectedTimeFilter('all');
        } else {
          setSelectedStatuses(['in_progress']);
          setSelectedTimeFilter('all');
        }
      },
    },
    {
      key: 'completed',
      label: 'Completed',
      value: taskStats.completed,
      bgClass: 'bg-green-50',
      labelClass: 'text-xs text-green-700',
      valueClass: 'text-lg font-semibold text-green-700',
      isActive:
        selectedStatuses.length === 1 &&
        selectedStatuses[0] === 'complete' &&
        selectedTimeFilter === 'all',
      onClick: () => {
        if (
          selectedStatuses.length === 1 &&
          selectedStatuses[0] === 'complete' &&
          selectedTimeFilter === 'all'
        ) {
          setSelectedStatuses([]);
          setSelectedTimeFilter('all');
        } else {
          setSelectedStatuses(['complete']);
          setSelectedTimeFilter('all');
        }
      },
    },
    {
      key: 'overdue',
      label: 'Overdue',
      value: taskStats.overdue,
      bgClass: 'bg-red-50',
      labelClass: 'text-xs text-red-700',
      valueClass: 'text-lg font-semibold text-red-700',
      isActive: selectedTimeFilter === 'overdue',
      onClick: () => {
        if (selectedTimeFilter === 'overdue') {
          setSelectedTimeFilter('all');
        } else {
          setSelectedStatuses([]);
          setSelectedTimeFilter('overdue');
        }
      },
    },
    {
      key: 'dueToday',
      label: 'Due Today',
      value: taskStats.dueToday,
      bgClass: 'bg-orange-50',
      labelClass: 'text-xs text-orange-700',
      valueClass: 'text-lg font-semibold text-orange-700',
      isActive: selectedTimeFilter === 'dueToday',
      onClick: () => {
        if (selectedTimeFilter === 'dueToday') {
          setSelectedTimeFilter('all');
        } else {
          setSelectedStatuses([]);
          setSelectedTimeFilter('dueToday');
        }
      },
    },
    {
      key: 'dueThisWeek',
      label: 'This Week',
      value: taskStats.dueThisWeek,
      bgClass: 'bg-purple-50',
      labelClass: 'text-xs text-purple-700',
      valueClass: 'text-lg font-semibold text-purple-700',
      isActive: selectedTimeFilter === 'dueThisWeek',
      onClick: () => {
        if (selectedTimeFilter === 'dueThisWeek') {
          setSelectedStatuses([]);
          setSelectedTimeFilter('all');
        } else {
          setSelectedStatuses([]);
          setSelectedTimeFilter('dueThisWeek');
        }
      },
    },
  ];

  if (loading) {
    return <LoadingProgress message={loadingMessage} progress={50} />;
  }

  if (!selectedOrg || !user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Please select an organization</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
              My Tasks
            </h1>
            <p className="mt-1 text-sm text-gray-600 sm:text-base">
              Manage your personal task assignments
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchData}
              className="rounded-md border border-gray-200 p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
              title="Refresh tasks"
            >
              <ArrowPathIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() =>
                setViewMode(viewMode === 'kanban' ? 'list' : 'kanban')
              }
              className="rounded-md border border-gray-200 p-2 text-gray-600 transition-colors hover:bg-gray-100"
              title={
                viewMode === 'kanban'
                  ? 'Switch to List View'
                  : 'Switch to Kanban View'
              }
            >
              {viewMode === 'kanban' ? (
                <ListBulletIcon className="h-4 w-4" />
              ) : (
                <Squares2X2Icon className="h-4 w-4" />
              )}
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4" />
              <span className="hidden sm:inline">New Task</span>
              <span className="sm:hidden" aria-label="New Task"></span>
            </button>
          </div>
        </div>

        {/* Task Statistics - Scrollable on mobile */}
        <div className="mb-4 -mx-2 px-2 sm:mx-0 sm:px-0">
          <div className="flex gap-3 overflow-x-auto pb-2 sm:grid sm:grid-cols-4 sm:overflow-visible sm:pb-0 lg:grid-cols-7">
            {summaryCards.map((card) => (
              <button
                key={card.key}
                type="button"
                onClick={card.onClick}
                aria-pressed={card.isActive}
                className={`${card.bgClass} min-w-[100px] flex-shrink-0 rounded-lg px-3 py-2 text-left shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 sm:min-w-0 ${
                  card.isActive
                    ? 'ring-2 ring-blue-500'
                    : 'hover:ring-2 hover:ring-blue-300'
                }`}
              >
                <div className={card.labelClass}>{card.label}</div>
                <div className={card.valueClass}>{card.value}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Filters and View Toggle */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-4 w-4 text-gray-500" />
              <MultiSelectDropdown
                options={[
                  { value: 'not_started', label: 'Not Started' },
                  { value: 'in_progress', label: 'In Progress' },
                  { value: 'blocked', label: 'Blocked' },
                  { value: 'complete', label: 'Complete' },
                ]}
                value={selectedStatuses}
                onChange={(value) => {
                  setSelectedStatuses(value);
                  setSelectedTimeFilter('all');
                }}
                placeholder="All Status"
                className="min-w-[140px]"
              />
            </div>

            {/* Company Filter */}
            <MultiSelectDropdown
              options={companies.map((company) => ({
                value: company.id,
                label: company.name,
              }))}
              value={selectedCompanies}
              onChange={setSelectedCompanies}
              placeholder="All Companies"
              className="min-w-[160px]"
            />

            {/* OKR Filter */}
            <MultiSelectDropdown
              options={allOKRs.map((okr: any) => ({
                value: okr.id,
                label: okr.objective || 'Unnamed OKR',
              }))}
              value={selectedOKRs}
              onChange={setSelectedOKRs}
              placeholder="All OKRs"
              className="min-w-[160px]"
            />

            {/* Segment Filter */}
            <MultiSelectDropdown
              options={segments.map((segment: any) => ({
                value: segment.id,
                label: segment.name,
              }))}
              value={selectedSegments}
              onChange={setSelectedSegments}
              placeholder="All Segments"
              className="min-w-[160px]"
            />

            {/* Proposition Filter */}
            <MultiSelectDropdown
              options={propositions.map((proposition: any) => ({
                value: proposition.id,
                label: proposition.name,
              }))}
              value={selectedPropositions}
              onChange={setSelectedPropositions}
              placeholder="All Propositions"
              className="min-w-[160px]"
            />

            {/* Area Filter */}
            <MultiSelectDropdown
              options={areas
                .filter((area) => area.is_active)
                .map((area) => ({
                  value: area.id,
                  label: area.name,
                }))}
              value={selectedAreas}
              onChange={setSelectedAreas}
              placeholder="All Areas"
              className="min-w-[140px]"
            />

            {/* Clear Filters */}
            {filtersActive && (
              <button
                onClick={() => {
                  setSelectedAreas([]);
                  setSelectedStatuses([]);
                  setSelectedTimeFilter('all');
                  setSelectedCompanies([]);
                  setSelectedOKRs([]);
                  setSelectedSegments([]);
                  setSelectedPropositions([]);
                }}
                className="flex items-center gap-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700"
              >
                <XMarkIcon className="h-4 w-4" />
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Task View */}
      <div className="flex-1 overflow-auto p-2 sm:p-4 lg:p-6">
        {(tasks.length === 0 || filteredTasks.length === 0) && (
          <div className="mb-4 space-y-2">
            {tasks.length === 0 && (
              <div className="rounded-lg bg-white px-4 py-3 text-center text-sm text-gray-500">
                You have no tasks assigned to you.
              </div>
            )}
            {tasks.length > 0 && filteredTasks.length === 0 && (
              <div className="rounded-lg bg-white px-4 py-3 text-center text-sm text-gray-500">
                No tasks match the selected filters.
              </div>
            )}
          </div>
        )}

        {viewMode === 'kanban' ? (
          <TaskKanbanView
            tasks={filteredTasks}
            canEdit={canEdit}
            organizationId={selectedOrg.id}
            positions={positions}
            segments={segments}
            propositions={propositions}
            groups={groups}
            companies={companies}
            onTaskUpdate={handleTaskUpdate}
            onTaskCreate={handleTaskCreate}
            onTaskDelete={handleTaskDelete}
            hideCreateButton={true}
            preferenceNamespace="myTasks"
          />
        ) : (
          <TaskListView
            tasks={filteredTasks}
            canEdit={canEdit}
            organizationId={selectedOrg.id}
            positions={positions}
            segments={segments}
            propositions={propositions}
            groups={groups}
            companies={companies}
            onTaskUpdate={handleTaskUpdate}
            onTaskCreate={handleTaskCreate}
            onTaskDelete={handleTaskDelete}
            hideCreateButton={true}
          />
        )}
      </div>

      {/* Create Task Modal */}
      {showCreateModal && (
        <TaskModal
          task={null}
          organizationId={selectedOrg.id}
          onClose={() => setShowCreateModal(false)}
          onSave={handleTaskCreate}
          segments={segments}
          propositions={propositions}
          groups={groups}
          positions={positions}
          isAdmin={isAdmin}
          initialValues={{
            assigned_to_id: user.id,
          }}
        />
      )}
    </div>
  );
};

export default MyTasks;
